// ======================================================================
// FILE:
// src/modules/finance/transactions/contra_voucher/pages/ContraVoucherViewPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Contra Voucher - View
//
// PURPOSE:
// Display a Finance Contra Voucher in read-only mode.
//
// IMPORTANT:
// - No accounting logic
// - No direct database access
// - Uses contraVoucherService
// - Draft vouchers can be edited/submitted
// - Submitted vouchers can be approved
// - Approved vouchers can be posted
// ======================================================================

import React, {
    useEffect,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceStatusBadge
    from "../../../components/FinanceStatusBadge";

import contraVoucherService
    from "../services/contraVoucherService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function ContraVoucherViewPage() {

    const navigate =
        useNavigate();

    const {
        id
    } = useParams();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        voucher,
        setVoucher
    ] = useState(null);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");

    const [
        actionLoading,
        setActionLoading
    ] = useState(false);


    // ==================================================================
    // LOAD
    // ==================================================================

    useEffect(
        () => {

            loadVoucher();

        },
        [
            id
        ]
    );


    async function loadVoucher() {

        try {

            setLoading(true);

            setErrorMessage("");


            const response =
                await contraVoucherService
                    .getContraVoucher(
                        id
                    );


            setVoucher(
                response
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to load Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.message ||

                "Failed to load Contra Voucher."

            );

        }
        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // NAVIGATION
    // ==================================================================

    function handleBack() {

        navigate(
            "/finance/contra-vouchers"
        );

    }


    function handleEdit() {

        navigate(
            `/finance/contra-vouchers/edit/${id}`
        );

    }


    // ==================================================================
    // SUBMIT
    // ==================================================================

    async function handleSubmit() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await contraVoucherService
                .submitContraVoucher(

                    voucher.id,

                    voucher.company_id

                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to submit Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.message ||

                "Failed to submit Contra Voucher."

            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // APPROVE
    // ==================================================================

    async function handleApprove() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await contraVoucherService
                .approveContraVoucher(

                    voucher.id,

                    voucher.company_id

                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to approve Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.message ||

                "Failed to approve Contra Voucher."

            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // POST
    // ==================================================================

    async function handlePost() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await contraVoucherService
                .postContraVoucher(

                    voucher.id,

                    voucher.company_id

                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to post Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.message ||

                "Failed to post Contra Voucher."

            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // CONFIRMATIONS
    // ==================================================================

    async function confirmAndSubmit() {

        const confirmed =
            window.confirm(
                "Submit this Contra Voucher for approval?"
            );


        if (
            !confirmed
        ) {

            return;

        }


        await handleSubmit();

    }


    async function confirmAndApprove() {

        const confirmed =
            window.confirm(
                "Approve this Contra Voucher?"
            );


        if (
            !confirmed
        ) {

            return;

        }


        await handleApprove();

    }


    async function confirmAndPost() {

        const confirmed =
            window.confirm(
                "Post this Contra Voucher to the accounts?"
            );


        if (
            !confirmed
        ) {

            return;

        }


        await handlePost();

    }


    // ==================================================================
    // FORMATTERS
    // ==================================================================

    function formatDate(
        value
    ) {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        ).slice(
            0,
            10
        );

    }


    function formatDateTime(
        value
    ) {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        ).replace(
            "T",
            " "
        );

    }


    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,

            }
        );

    }


    // ==================================================================
    // LOADING
    // ==================================================================

    if (
        loading
    ) {

        return (

            <div
                className="
                    d-flex
                    justify-content-center
                    align-items-center
                    py-5
                "
            >

                <div
                    className="
                        spinner-border
                        text-primary
                    "
                    role="status"
                />

            </div>

        );

    }


    // ==================================================================
    // NOT FOUND / ERROR
    // ==================================================================

    if (
        !voucher
    ) {

        return (

            <>

                <FinancePageHeader

                    icon="fas fa-exchange-alt"

                    title="Contra Voucher"

                    subtitle={
                        "Contra Voucher Details"
                    }

                    breadcrumbs={[
                        {
                            label:
                                "Finance",

                            to:
                                "/finance",
                        },
                        {
                            label:
                                "Contra Voucher",

                            to:
                                "/finance/contra-vouchers",
                        },
                        {
                            label:
                                "View",
                        },
                    ]}

                    actions={

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleBack
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to List

                        </button>

                    }

                />


                <div
                    className="
                        alert
                        alert-danger
                        mt-3
                    "
                >

                    {
                        errorMessage ||
                        "Contra Voucher not found."
                    }

                </div>

            </>

        );

    }


    // ==================================================================
    // STATUS
    // ==================================================================

    const status =
        String(
            voucher.status ||
            ""
        ).toUpperCase();


    const approvalStatus =
        String(
            voucher.approval_status ||
            ""
        ).toUpperCase();


    // ==================================================================
    // ACTION VISIBILITY
    // ==================================================================

    const canEdit =
        status === "DRAFT";


    const canSubmit =
        status === "DRAFT";


    const canApprove =
        status === "SUBMITTED";


    const canPost =
        status === "APPROVED";


    // ==================================================================
    // LINES
    // ==================================================================

    const lines =
        Array.isArray(
            voucher.lines
        )
            ? voucher.lines
            : [];


    // ==================================================================
    // TOTALS
    // ==================================================================

    const totalTransactionAmount =
        lines.reduce(
            (
                total,
                line
            ) =>
                total +
                (
                    Number(
                        line.transaction_amount
                    ) || 0
                ),
            0
        );


    const totalBaseAmount =
        lines.reduce(
            (
                total,
                line
            ) =>
                total +
                (
                    Number(
                        line.base_amount
                    ) || 0
                ),
            0
        );


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-exchange-alt"

                title={
                    voucher.voucher_no ||
                    "Contra Voucher"
                }

                subtitle={
                    "Contra Voucher Details"
                }

                breadcrumbs={[
                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Contra Voucher",

                        to:
                            "/finance/contra-vouchers",
                    },

                    {
                        label:
                            voucher.voucher_no ||
                            "View",
                    },
                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                            flex-wrap
                        "
                    >

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleBack
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to List

                        </button>


                        {
                            canEdit && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-warning
                                    "

                                    onClick={
                                        handleEdit
                                    }

                                >

                                    <i
                                        className="
                                            fas
                                            fa-edit
                                            me-1
                                        "
                                    />

                                    Edit

                                </button>

                            )
                        }


                        {
                            canSubmit && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-primary
                                    "

                                    onClick={
                                        confirmAndSubmit
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    {
                                        actionLoading ? (

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                        ) : (

                                            <i
                                                className="
                                                    fas
                                                    fa-paper-plane
                                                    me-1
                                                "
                                            />

                                        )
                                    }

                                    Submit

                                </button>

                            )
                        }


                        {
                            canApprove && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-success
                                    "

                                    onClick={
                                        confirmAndApprove
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    {
                                        actionLoading ? (

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                        ) : (

                                            <i
                                                className="
                                                    fas
                                                    fa-check
                                                    me-1
                                                "
                                            />

                                        )
                                    }

                                    Approve

                                </button>

                            )
                        }


                        {
                            canPost && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-dark
                                    "

                                    onClick={
                                        confirmAndPost
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    {
                                        actionLoading ? (

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                        ) : (

                                            <i
                                                className="
                                                    fas
                                                    fa-check-double
                                                    me-1
                                                "
                                            />

                                        )
                                    }

                                    Post

                                </button>

                            )
                        }

                    </div>

                }

            />


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        <i
                            className="
                                fas
                                fa-exclamation-triangle
                                me-2
                            "
                        />

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                STATUS SUMMARY
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <div
                        className="
                            row
                            g-3
                        "
                    >

                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Voucher No
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.voucher_no ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Voucher Date
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatDate(
                                        voucher.voucher_date
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Status
                            </div>

                            <div>

                                <FinanceStatusBadge

                                    type="journal"

                                    value={
                                        voucher.status
                                    }

                                />

                            </div>

                        </div>


                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Approval Status
                            </div>

                            <div>

                                <FinanceStatusBadge

                                    type="approval"

                                    value={
                                        voucher.approval_status
                                    }

                                />

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        fw-semibold
                    "
                >

                    <i
                        className="
                            fas
                            fa-file-invoice
                            me-2
                        "
                    />

                    Contra Voucher Details

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-4"
                    >

                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Company
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.company_name ||
                                    voucher.company_id ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                From Account
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.from_account_name ||
                                    voucher.from_account_code ||
                                    voucher.from_account_id ||
                                    "-"
                                }
                            </div>

                            {
                                voucher.from_account_code && (

                                    <div
                                        className="text-muted small"
                                    >
                                        {
                                            voucher.from_account_code
                                        }
                                    </div>

                                )
                            }

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                To Account
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.to_account_name ||
                                    voucher.to_account_code ||
                                    voucher.to_account_id ||
                                    "-"
                                }
                            </div>

                            {
                                voucher.to_account_code && (

                                    <div
                                        className="text-muted small"
                                    >
                                        {
                                            voucher.to_account_code
                                        }
                                    </div>

                                )
                            }

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Currency
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.currency_code ||
                                    voucher.currency_id ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Exchange Rate
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.exchange_rate ??
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Transaction Amount
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatAmount(
                                        voucher.transaction_amount
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Base Amount
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatAmount(
                                        voucher.base_amount
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Reference No
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.reference_no ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Reference Date
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatDate(
                                        voucher.reference_date
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="col-12"
                        >

                            <div
                                className="text-muted small"
                            >
                                Remarks
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.remarks ||
                                    "-"
                                }
                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ALLOCATION DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        fw-semibold
                    "
                >

                    <i
                        className="
                            fas
                            fa-list
                            me-2
                        "
                    />

                    Contra Allocation Details

                </div>


                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                mb-0
                                align-middle
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th>#</th>

                                    <th>
                                        Amount
                                    </th>

                                    <th>
                                        Base Amount
                                    </th>

                                    <th>
                                        Cost Centre
                                    </th>

                                    <th>
                                        Project
                                    </th>

                                    <th>
                                        Department
                                    </th>

                                    <th>
                                        Description
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan={7}
                                                className="
                                                    text-center
                                                    text-muted
                                                "
                                            >
                                                No allocation lines.
                                            </td>

                                        </tr>

                                    ) : (

                                        lines.map(
                                            (
                                                line,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        line.id ||
                                                        index
                                                    }
                                                >

                                                    <td>
                                                        {
                                                            line.sequence_no ||
                                                            index + 1
                                                        }
                                                    </td>

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >
                                                        {
                                                            formatAmount(
                                                                line.transaction_amount
                                                            )
                                                        }
                                                    </td>

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >
                                                        {
                                                            formatAmount(
                                                                line.base_amount
                                                            )
                                                        }
                                                    </td>

                                                    <td>
                                                        {
                                                            line.cost_center_name ||
                                                            line.cost_center_id ||
                                                            "-"
                                                        }
                                                    </td>

                                                    <td>
                                                        {
                                                            line.project_name ||
                                                            line.project_id ||
                                                            "-"
                                                        }
                                                    </td>

                                                    <td>
                                                        {
                                                            line.department_name ||
                                                            line.department_id ||
                                                            "-"
                                                        }
                                                    </td>

                                                    <td>
                                                        {
                                                            line.description ||
                                                            "-"
                                                        }
                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>


                            <tfoot
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        colSpan={1}
                                        className="text-end"
                                    >
                                        Total
                                    </th>

                                    <th
                                        className="text-end"
                                    >
                                        {
                                            formatAmount(
                                                totalTransactionAmount
                                            )
                                        }
                                    </th>

                                    <th
                                        className="text-end"
                                    >
                                        {
                                            formatAmount(
                                                totalBaseAmount
                                            )
                                        }
                                    </th>

                                    <th
                                        colSpan={4}
                                    />

                                </tr>

                            </tfoot>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ACCOUNTING INFORMATION
            ========================================================== */}

            {
                (
                    voucher.journal_header_id ||
                    voucher.journal_no
                ) && (

                    <div
                        className="
                            card
                            shadow-sm
                            border-0
                            mt-3
                        "
                    >

                        <div
                            className="
                                card-header
                                fw-semibold
                            "
                        >

                            <i
                                className="
                                    fas
                                    fa-book
                                    me-2
                                "
                            />

                            Accounting Information

                        </div>


                        <div
                            className="card-body"
                        >

                            <div
                                className="row g-4"
                            >

                                <div
                                    className="col-lg-4"
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Journal Header ID
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >
                                        {
                                            voucher.journal_header_id ||
                                            "-"
                                        }
                                    </div>

                                </div>


                                <div
                                    className="col-lg-4"
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Journal No
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >
                                        {
                                            voucher.journal_no ||
                                            "-"
                                        }
                                    </div>

                                </div>


                                <div
                                    className="col-lg-4"
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Posted By
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >
                                        {
                                            voucher.posted_by ||
                                            "-"
                                        }
                                    </div>

                                </div>


                                <div
                                    className="col-lg-4"
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Posted At
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >
                                        {
                                            formatDateTime(
                                                voucher.posted_at
                                            )
                                        }
                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ==========================================================
                APPROVAL INFORMATION
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        fw-semibold
                    "
                >

                    <i
                        className="
                            fas
                            fa-user-check
                            me-2
                        "
                    />

                    Approval Information

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-4"
                    >

                        <div
                            className="col-lg-4"
                        >

                            <div
                                className="text-muted small"
                            >
                                Approval Status
                            </div>

                            <FinanceStatusBadge

                                type="approval"

                                value={
                                    approvalStatus
                                }

                            />

                        </div>


                        <div
                            className="col-lg-4"
                        >

                            <div
                                className="text-muted small"
                            >
                                Approved By
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.approved_by ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-4"
                        >

                            <div
                                className="text-muted small"
                            >
                                Approved At
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatDateTime(
                                        voucher.approved_at
                                    )
                                }
                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                AUDIT INFORMATION
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        fw-semibold
                    "
                >

                    <i
                        className="
                            fas
                            fa-history
                            me-2
                        "
                    />

                    Audit Information

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-4"
                    >

                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Created By
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.created_by ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Created At
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatDateTime(
                                        voucher.created_at
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Updated By
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    voucher.updated_by ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="col-lg-3 col-md-6"
                        >

                            <div
                                className="text-muted small"
                            >
                                Updated At
                            </div>

                            <div
                                className="fw-semibold"
                            >
                                {
                                    formatDateTime(
                                        voucher.updated_at
                                    )
                                }
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </>

    );

}